import {
    Flex,
    Select,
    IconButton,
    Icon,
    useToast,
    Text,
    Button,
    Checkbox,
    Popover,
    PopoverTrigger,
    PopoverContent,
    Stack,
    PopoverBody,
    HStack,
    chakra,
    Box,
    Grid,
    GridItem,
} from '@chakra-ui/react';
import { useExportSectoExcelMutation } from 'core/API/sectoApi';
import { useRetailers } from 'core/redux/selectors/dataSelectors';
import FileSaver from 'file-saver';
import { useTranslation } from 'helpers/languages/translations';
import GlobalFilter from 'plugins/DITable/filters/GlobalFilter';
import { useState, useEffect, ChangeEvent } from 'react';
import { EXCEL_FILE_TYPE } from 'shared/const';
import icons from 'shared/icons';
import { tableToolbarStyle, iconTableStyle } from 'shared/styles';
import { IRetailer } from 'shared/types';
import { BsLayoutThreeColumns } from 'react-icons/bs';
import { MdOutlinePushPin } from 'react-icons/md';
import { capitalize } from 'lodash';

import { LIST_FIELDS } from './const';
import { TSectoViewer } from './SectoContainer';

const SectoToolbar = ({
    tableInstance,
    toolId,
    selectedRetailer,
    setSelectedRetailer,
    selectedHiddenHeader,
    setSelectedHiddenHeader,
}: TSectoViewer & { toolId: number }) => {
    const { retailers, isLoading: isRetailerLoading, isError: isRetailerError } = useRetailers();
    const translate = useTranslation();
    const [exportSectoMutation, { isLoading: isExportLoading }] = useExportSectoExcelMutation();
    const toast = useToast();

    const [retailerList, setRetailerList] = useState<IRetailer[]>([]);

    const { state, setGlobalFilter } = tableInstance;
    const ArrowDownIcon = icons.arrowDown;

    useEffect(() => {
        if (retailers && !isRetailerLoading && !isRetailerError) {
            const retailerListData = [...retailers].sort((a, b) => a.name.localeCompare(b.name));
            setRetailerList(retailerListData);
        }

        const newHiddenColumn = { ...selectedHiddenHeader };
        LIST_FIELDS.forEach((col) => {
            newHiddenColumn[col] = localStorage.getItem(col) === 'true';
        });
        setSelectedHiddenHeader(newHiddenColumn);
    }, [isRetailerLoading, isRetailerError]);

    const onChangeRetailer = (e: ChangeEvent<HTMLSelectElement>) => {
        setSelectedRetailer(Number(e.target.value));
    };

    const onClickDownloadSecto = async () => {
        try {
            const res = await exportSectoMutation({ toolId, retailer: selectedRetailer });
            if ('error' in res) throw new Error('something wrong occured when exporting users excel');

            const blob = new Blob([res.data], {
                type: EXCEL_FILE_TYPE,
            });

            const timeElapsed = Date.now();
            const today = new Date(timeElapsed);
            FileSaver.saveAs(blob, `secto_${today.toISOString()}.xlsx`);
            toast({
                title: 'succesfully exported secto excel',
                status: 'success',
                isClosable: true,
            });
        } catch (error) {
            toast({
                title: (error as Error).message,
                status: 'error',
                isClosable: true,
            });
        }
    };

    const toogleColumn = (column: string) => () => {
        const newHiddenColumn = { ...selectedHiddenHeader };

        newHiddenColumn[column] = !newHiddenColumn[column];
        localStorage.setItem(column, newHiddenColumn[column] ? 'true' : 'false');

        setSelectedHiddenHeader(newHiddenColumn);
    };

    return (
        <Flex {...tableToolbarStyle}>
            <GlobalFilter globalFilter={state.globalFilter} setGlobalFilter={setGlobalFilter} />
            <Flex>
                <Select
                    value={selectedRetailer}
                    onChange={onChangeRetailer}
                    bg="white"
                    borderColor="gray.200"
                    size="sm"
                    mr={4}
                    placeholder="Select retailer"
                >
                    {retailerList.map((retailer) => (
                        <option value={retailer.id}>{retailer.name}</option>
                    ))}
                </Select>
            </Flex>
            <IconButton
                {...iconTableStyle}
                isLoading={isExportLoading}
                onClick={onClickDownloadSecto}
                aria-label="download list"
                size="sm"
                icon={<Icon as={ArrowDownIcon} />}
            />
            <Popover>
                <PopoverTrigger>
                    <IconButton
                        {...iconTableStyle}
                        isLoading={isExportLoading}
                        aria-label="download list"
                        size="sm"
                        icon={<Icon as={BsLayoutThreeColumns} />}
                    />
                </PopoverTrigger>
                <PopoverContent width={190}>
                    <PopoverBody>
                        <Stack spacing={2}>
                            {LIST_FIELDS.map((item) => (
                                <Box onClick={toogleColumn(item)}>
                                    <Grid
                                        padding={'2px 6px'}
                                        border="1px solid #bebec6"
                                        borderRadius="6px"
                                        _hover={{ bg: 'gray.100' }}
                                        templateColumns="repeat(2, 1fr)"
                                        gap={2}
                                    >
                                        <GridItem w={100} colSpan={1}>
                                            <Text fontSize="sm" as="b">
                                                {capitalize(translate(`secto.column.${item}`) as string)}
                                            </Text>
                                        </GridItem>
                                        <GridItem w={10} colSpan={1}>
                                            <HStack paddingTop={1}>
                                                <MdOutlinePushPin color="#bebec6" />
                                                <Checkbox
                                                    onChange={toogleColumn(item)}
                                                    isChecked={localStorage.getItem(item) === 'true'}
                                                />
                                            </HStack>
                                        </GridItem>
                                    </Grid>
                                </Box>
                            ))}
                        </Stack>
                    </PopoverBody>
                </PopoverContent>
            </Popover>
        </Flex>
    );
};

export default SectoToolbar;
